/**
 * MembersDrawer — painel lateral (direita) com a lista de integrantes da conversa.
 *
 * Exibe avatar com iniciais + nome + e-mail de cada membro,
 * com estados de carregamento e lista vazia.
 */
import { Avatar, Box, Drawer, Group, Loader, Stack, Text } from '@mantine/core'
import { useConversationMembers } from '@/hooks/useChat'

interface Props {
  cid: number
  opened: boolean
  onClose: () => void
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function MembersDrawer({ cid, opened, onClose }: Props) {
  const { data: members, isLoading } = useConversationMembers(cid)

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={320}
      title={
        <Text fw={600} size="sm">
          Integrantes
        </Text>
      }
      styles={{
        header: {
          borderBottom: '1px solid var(--ai-border-light, var(--mantine-color-gray-2))',
          paddingBottom: 'var(--mantine-spacing-sm)',
        },
      }}
    >
      <Stack gap="xs" pt="sm">
        {isLoading && (
          <Group justify="center" py="xl">
            <Loader size="sm" type="dots" color="teal" />
            <Text size="sm" c="dimmed">
              Carregando integrantes…
            </Text>
          </Group>
        )}

        {!isLoading && (!members || members.length === 0) && (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            Nenhum integrante encontrado.
          </Text>
        )}

        {members?.map((member) => (
          <Group key={member.id} gap="sm" wrap="nowrap" px="xs" py={6}>
            <Avatar
              size="md"
              radius="xl"
              color="teal"
              variant="filled"
              src={member.avatar_url ?? undefined}
              style={{ flexShrink: 0 }}
            >
              {getInitials(member.name)}
            </Avatar>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" fw={500} lineClamp={1}>
                {member.name}
              </Text>
              <Text size="xs" c="dimmed" lineClamp={1}>
                {member.email}
              </Text>
            </Box>
          </Group>
        ))}
      </Stack>
    </Drawer>
  )
}
